import { useMediaQuery } from '@mui/material'
import Button from '@mui/material/Button'
import FormControl from '@mui/material/FormControl'
import FormControlLabel from '@mui/material/FormControlLabel'
import Grid from '@mui/material/Grid2'
import Radio from '@mui/material/Radio'
import RadioGroup from '@mui/material/RadioGroup'
import Stack from '@mui/material/Stack'
import { styled } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import { map, round, trim } from 'lodash'
import { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { TariffUpgradeDialog } from '../../../../dialogs/TariffUpgradeDialog'
import { useProfileStore } from '../../../../store/storeProfile'
import { TariffDTO } from '../../../../swagger/dto'
import { theme } from '../../../../theme'
import { PackageDescription } from './PackageDescription'
import { PriceInfo } from './PriceInfo'
import { useLocalizedTariffName } from '../../../../utils/getLocalizedTariffName'

type Props = {
    tariff: TariffDTO
    index?: number
    activeSubscriptionCheckboxId: string
    onToggleSubscription: (tariffId: string) => void
}
const Wrapper = ({ isMobile, className, children }) => {
    if (isMobile) {
        return (
            <div
                style={{
                    minWidth: '100%',
                }}
                className={className}>
                {children}
            </div>
        )
    } else {
        return (
            <Grid
                size={{ xs: 12, sm: 12, lg: 4 }}
                sx={{ width: '100%' }}
                spacing={0}
                className={className}>
                {children}
            </Grid>
        )
    }
}

const Icon = styled('span')(({ theme }) => ({
    padding: 0,
    borderRadius: '50%',
    width: '20px',
    height: '20px',
    boxShadow: 'inset 0 0 0 1px rgba(16,22,26,.2), inset 0 -1px 0 rgba(16,22,26,.1)',
    backgroundColor: '#FFF',
    '.Mui-focusVisible &': {
        outline: '10px auto rgba(19,124,189,.6)',
        outlineOffset: 10,
    },
    'input:hover ~ &': {
        backgroundColor: '#ebf1f5',
    },
}))

const CheckedIcon = styled(Icon)({
    backgroundColor: '#006CFD',
    backgroundImage: 'linear-gradient(180deg,hsla(0,0%,100%,.1),hsla(0,0%,100%,0))',
    '&::before': {
        display: 'block',
        width: 20,
        height: 20,
        backgroundImage: 'radial-gradient(#fff,#fff 28%,transparent 32%)',
        content: '""',
    },
    'input:hover ~ &': {
        backgroundColor: '#106ba3',
    },
})

function RadioTheme(props) {
    return <Radio disableRipple checkedIcon={<CheckedIcon />} icon={<Icon />} {...props} />
}

export const PackageCard: FC<Props> = ({ tariff, index, activeSubscriptionCheckboxId }) => {
    const { t } = useTranslation()
    const isMobile = useMediaQuery(theme.breakpoints.down('md'))
    const isFree = index === 'Бесплатный'
    const { title, price, id, tariff_type, bonus_coins, restrictions } = tariff[0]
    const { deposit, userTariff } = useProfileStore((state) => state)
    const [isOpenTariffUpgradeDialog, setIsOpenTariffUpgradeDialog] = useState(false)
    const [coins, setСoins] = useState(bonus_coins + price)
    const [tariffId, setTariffId] = useState(id)
    const [priceByRobokassa, setPriceByRobokassa] = useState(price)

    const handleCloseTariffUpgradeDialog = () => {
        setIsOpenTariffUpgradeDialog(false)
    }

    const handleSendPayment = () => {
        if (
            userTariff?.tariff.tariff_type === 'PROFESSIONAL' &&
            tariff[0].tariff_type === 'START'
        ) {
            setIsOpenTariffUpgradeDialog(true)
        } else {
            deposit({
                amount: +trim(`${priceByRobokassa}`, ' '),
                tariff_uuid: tariffId,
            })
        }
    }

    const handleChange = (id, price, bonus_coins) => {
        setTariffId(id)
        setСoins(price + bonus_coins)
        setPriceByRobokassa(price)
    }

    const className = `keen-slider__slide number-slide${index + 1}`

    const localizedTariffName = useLocalizedTariffName()

    return (
        <Wrapper isMobile={isMobile} className={className}>
            <Stack
                spacing={0}
                direction="column"
                justifyContent="space-between"
                sx={{
                    background: isMobile ? '#F5F5F5' : 'none',
                    width: '100%',
                    textAlign: 'left',
                    p: isMobile ? 2 : 3,
                    borderTop: '1px solid #E9EAED',
                    borderBottom: '1px solid #E9EAED',
                    borderRight: index === 'Профи' ? 'none' : '1px solid #E9EAED',
                    position: 'relative',
                    overflow: 'hidden',
                    height: '100%',
                    borderRadius: isMobile ? '12px' : 0,
                    '&:hover': {
                        borderRadius: isMobile ? '12px' : 0,
                    },
                }}>
                <Stack direction="column">
                    <Stack
                        direction="row"
                        sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography gutterBottom variant="body1" component="div">
                            {localizedTariffName(tariff_type)}
                        </Typography>

                        {userTariff?.tariff?.title === title && (
                            <Typography variant="body2">{t('tariff.currentLevel')}</Typography>
                        )}
                    </Stack>
                    <PriceInfo
                        isMobile={isMobile}
                        price={priceByRobokassa}
                        discountedPrice={price}
                    />

                    {isMobile && (
                        <PackageDescription
                            isMobile={isMobile}
                            tariff_type={tariff_type}
                            restrictions={restrictions}
                        />
                    )}
                    {!isFree && (
                        <FormControl>
                            <RadioGroup
                                defaultValue={coins}
                                sx={{
                                    marginTop: '8px',
                                    borderRadius: '12px',
                                    border: '1px solid #E5E5E7',
                                }}>
                                {map(tariff, (item, key) => {
                                    const RadioLabel = () => (
                                        <Stack
                                            direction="row"
                                            sx={{ justifyContent: 'space-between' }}>
                                            <Typography variant="body2">
                                                {item.price + item.bonus_coins} {t('tariff.coins')}
                                            </Typography>
                                            {key !== 0 && (
                                                <Typography
                                                    variant="body2"
                                                    sx={{ color: '#006AD8', fontSize: '13px' }}>
                                                    {t('tariff.moreProfit', {
                                                        percent: round((item.bonus_coins / item.price) * 100)
                                                    })}
                                                </Typography>
                                            )}
                                        </Stack>
                                    )

                                    return (
                                        <FormControlLabel
                                            className="package-card_radio-group_label"
                                            sx={{
                                                borderTopLeftRadius: key == 0 ? '12px' : 'none',
                                                borderTopRightRadius: key == 0 ? '12px' : 'none',
                                                borderBottomRightRadius: key == 2 ? '12px' : 'none',
                                                borderBottomLeftRadius: key == 2 ? '12px' : 'none',
                                                margin: '0px',
                                                borderBottom:
                                                    key < 2 ? '1px solid #E5E5E7' : 'none',
                                            }}
                                            onChange={() =>
                                                handleChange(item.id, item.price, item.bonus_coins)
                                            }
                                            value={item.price + item.bonus_coins}
                                            control={<RadioTheme />}
                                            label={<RadioLabel />}
                                        />
                                    )
                                })}
                            </RadioGroup>
                        </FormControl>
                    )}

                    {!isMobile && (
                        <PackageDescription
                            isMobile={isMobile}
                            tariff_type={tariff_type}
                            restrictions={restrictions}
                        />
                    )}
                </Stack>
                {isFree ? (
                    <Typography
                        variant="body2"
                        sx={{ color: '#5A5C5E', mb: '12px', mt: 3, p: isMobile ? 0 : '10px 24px' }}>
                        {t('tariff.freeDescription')}
                    </Typography>
                ) : (
                    <Button
                        disabled={isFree}
                        sx={{ p: '10px 24px', mb: '12px', mt: 3, fontSize: '13px' }}
                        color="primary"
                        variant="contained"
                        fullWidth
                        onClick={handleSendPayment}>
                        {t('tariff.replenishBalance', { coins })}
                    </Button>
                )}
            </Stack>
            <TariffUpgradeDialog
                open={isOpenTariffUpgradeDialog}
                handleClose={handleCloseTariffUpgradeDialog}
                handleSubmit={() => {
                    deposit({
                        amount: +trim(`${priceByRobokassa}`, ' '),
                        tariff_uuid: tariffId,
                    })
                }}
            />
        </Wrapper>
    )
}
